"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { formatFileSize } from "@/lib/helpers";
import { useApi } from "@/lib/pariette";
import { City, Country } from "@/lib/types";
import { cn } from "@/lib/utils";
import { AlertCircle, Building2, Check, CheckCircle2, FileText, Loader2, MapPin, Upload } from "lucide-react";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Link from "next/link";
import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Autocomplete from "./Autocomplete";
import { CityAutoComplete } from "./ParietteCreateModal";

interface FormData {
  title: string;
  inv_period: string;
  mwst_office: string;
  mwst_number: string;
  user_name: string;
  address_1: string;
  postal_code: string;
  city: string;
  country: string;
  tel1: string;
  tel2: string;
  email1: string;
  fax: string;
  store_type: string;
  tax_document: number | null;
  identity_document: number | null;
  tax_document_2: number | null;
  identity_document_2: number | null;
  other_document: number | null;
  other_document_2: number | null;
  note: string;
}

const documentTypes = {
  tax_document: "tax_document",
  tax_document_2: "tax_document_2",
  identity_document: "identity_document",
  identity_document_2: "identity_document_2",
  other_document: "other_document",
  other_document_2: "other_document_2",
} as const;
type documentTypes = (typeof documentTypes)[keyof typeof documentTypes];

const RegisterForm = () => {
  const t = useTranslations("register");
  const tGeneral = useTranslations("general");
  const tForm = useTranslations("form");

  const periods = [
    {
      label: t("periods.monthly"),
      value: "1",
    },
    {
      label: t("periods.weekly"),
      value: "2",
    },
  ];

  const types = [
    {
      label: t("types.handy_shop"),
      value: "Handy Shop",
    },
    {
      label: t("types.kiosk"),
      value: "Kiosk",
    },
    {
      label: t("types.super_market"),
      value: "Super Markt",
    },
    {
      label: t("types.other"),
      value: "Andere",
    },
  ];

  const [currentStep, setCurrentStep] = useState(1);
  const [errors, setErrors] = useState<{ field: keyof FormData; message: string }[]>([]);
  const { $get, $post } = useApi();
  const [cities, setCities] = useState<City[]>([]);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loader, setLoader] = useState<keyof FormData | "submit" | false>(false);
  const [formData, setFormData] = useState<FormData>({
    title: "",
    inv_period: "",
    mwst_office: "",
    mwst_number: "",
    user_name: "",
    address_1: "",
    postal_code: "",
    city: "",
    country: "",
    tel1: "",
    tel2: "",
    email1: "",
    fax: "",
    store_type: "",
    tax_document: null,
    identity_document: null,
    tax_document_2: null,
    identity_document_2: null,
    other_document: null,
    other_document_2: null,
    note: "",
  });

  const fetchCity = useCallback(async (m: string) => {
    await $get(`city?country=${m}`, { public: true }).then((res) => {
      setCities(res.data);
    });
  }, [$get]);

  const getCountries = useCallback(async () => {
    await $get("country", { public: true }).then((res) => {
      setCountries(res.data);
    });
  }, [$get]);

  const updateFormData = (field: keyof FormData, value: string | number | null) => {
    const fieldEl = document.getElementById(field in documentTypes ? `${field}_label` : field);
    if (fieldEl) {
      fieldEl.classList.remove("border-red-500");
    }
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors.length > 0) {
      setErrors([]);
    }
  };

  const handleFileChange = async (field: documentTypes, file: File | null) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      setErrors([{ field, message: t("errors.file_too_large") }]);
      return;
    }

    updateFormData(field, null);

    const formData = new FormData();
    formData.append("uploadFile", file);
    setLoader(field);
    await $post("store-application-document", formData, { public: true })
      .then((res) => {
        if (res.data.id) {
          updateFormData(field, res.data.id as number);
        }
      })
      .finally(() => setLoader(false));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateStep(currentStep)) return;
    const data = formData;
    setLoader("submit");
    await $post("store-application", { ...data }, { public: true })
      .then((res) => {
        if (res.status) {
          setCurrentStep(4);
        }
      })
      .finally(() => setLoader(false));
  };

  const validateStep = (step: number): boolean => {
    const newErrors: { field: keyof FormData; message: string }[] = [];

    if (step === 1) {
      if (!formData.title.trim()) newErrors.push({ field: "title", message: "Shop-Titel ist erforderlich" });
      if (!formData.user_name.trim()) newErrors.push({ field: "user_name", message: "Vollständiger Name ist erforderlich" });
      if (!formData.email1.trim()) {
        newErrors.push({ field: "email1", message: "E-Mail-Adresse ist erforderlich" });
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email1)) {
        newErrors.push({ field: "email1", message: "Bitte geben Sie eine gültige E-Mail-Adresse ein" });
      }
      if (!formData.tel1.trim()) newErrors.push({ field: "tel1", message: "Telefonnummer ist erforderlich" });
    }

    if (step === 2) {
      if (!formData.address_1.trim()) newErrors.push({ field: "address_1", message: "Straße und Hausnummer ist erforderlich" });
      if (!formData.postal_code.trim()) newErrors.push({ field: "postal_code", message: "Postleitzahl ist erforderlich" });
      if (!formData.city.trim()) newErrors.push({ field: "city", message: "Stadt ist erforderlich" });
      if (!formData.country) newErrors.push({ field: "country", message: "Land ist erforderlich" });
    }

    if (step === 3) {
      if (!formData.inv_period) newErrors.push({ field: "inv_period", message: "Rechnungsperiode ist erforderlich" });
      // if (!formData.mwst_office.trim()) newErrors.push({ field: "mwst_office", message: "Finanzamt ist erforderlich" });
      // if (!formData.mwst_number.trim()) newErrors.push({ field: "mwst_number", message: "Umsatzsteuer-Identifikationsnummer ist erforderlich" });
      if (!formData.store_type) newErrors.push({ field: "store_type", message: "Ladentyp ist erforderlich" });
      if (!formData.tax_document) newErrors.push({ field: "tax_document", message: "Steuerdokument ist erforderlich" });
      if (!formData.tax_document_2) newErrors.push({ field: "tax_document_2", message: "Steuerdokument 2 ist erforderlich" });
      if (!formData.identity_document) newErrors.push({ field: "identity_document", message: "Ausweisdokument ist erforderlich" });
      if (!formData.identity_document_2) newErrors.push({ field: "identity_document_2", message: "Ausweisdokument 2 ist erforderlich" });
    }

    newErrors.forEach((error) => {
      const fieldEl = document.getElementById(error.field in documentTypes ? `${error.field}_label` : error.field);
      if (fieldEl) {
        fieldEl.classList.add("border-red-500");
      }
    });

    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      if (currentStep < 3) setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setErrors([]);
    }
  };

  useEffect(() => {
    if (currentStep === 2 && countries.length === 0) {
      getCountries();
    }
  }, [currentStep]);

  return (
    <div className="container mx-auto px-4 py-8 max-w-4xl">
      <div className="mb-8 text-center">
        <Link href="/" className="inline-block">
          <div className="flex items-center justify-center gap-3 hover:opacity-80 transition-opacity">
            <Image src={"/aysoft_logo.svg"} alt="Pariette" width={140} height={70} priority className="hidden dark:block" />
            <Image src={"/aysoft_logo_red.svg"} alt="Pariette" width={140} height={70} priority className="block dark:hidden" />
          </div>
        </Link>
      </div>

      <form onSubmit={handleSubmit}>
        <Card className="shadow-lg">
          <CardHeader className="space-y-6">
            <div className="text-center">
              <CardTitle className="text-3xl mb-2">Geschäftsregistrierung</CardTitle>
              <CardDescription>Registrieren Sie Ihr Unternehmen in wenigen Schritten</CardDescription>
            </div>

            <div className="flex items-center justify-between">
              {[1, 2, 3].map((step) => (
                <div key={step} className={cn("flex items-center", (step === 1 || step === 3) && "flex-1")}>
                  {step === 3 && <div className={`h-1 flex-1 mx-2 transition-colors ${currentStep > step ? "bg-primary" : "bg-muted"}`} />}
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold transition-colors ${currentStep >= step ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"
                        }`}
                    >
                      {currentStep > step ? <CheckCircle2 className="w-5 h-5" /> : step}
                    </div>
                    <span className="text-xs mt-2 text-center hidden sm:block">
                      {step === 1 && "Grunddaten"}
                      {step === 2 && "Anschriftendaten"}
                      {step === 3 && "Dokumente & Identitätsnachweis"}
                    </span>
                  </div>
                  {step === 1 && <div className={`h-1 flex-1 mx-2 transition-colors ${currentStep > step ? "bg-primary" : "bg-muted"}`} />}
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <h3 className="font-semibold text-lg flex items-center gap-2">
                {currentStep === 1 && (
                  <>
                    <Building2 className="w-5 h-5" /> Grunddaten
                  </>
                )}
                {currentStep === 2 && (
                  <>
                    <MapPin className="w-5 h-5" /> {t("steps.address")}
                  </>
                )}
                {currentStep === 3 && (
                  <>
                    <FileText className="w-5 h-5" /> {t("steps.documents")}
                  </>
                )}
              </h3>
              {currentStep !== 4 && <p className="text-sm text-muted-foreground mt-1">{t("form.step_of", { step: currentStep })}</p>}
            </div>
          </CardHeader>

          <CardContent className="space-y-6">
            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  <ul className="list-disc list-inside space-y-1">
                    {errors.map((error, index) => (
                      <li key={index}>{error.message}</li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="title">{t("form.shop_title")} *</Label>
                  <Input id="title" value={formData.title} onChange={(e) => updateFormData("title", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="user_name">{t("form.full_name")} *</Label>
                  <Input id="user_name" value={formData.user_name} onChange={(e) => updateFormData("user_name", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email1">{tForm("email")} *</Label>
                  <Input id="email1" type="email" value={formData.email1} onChange={(e) => updateFormData("email1", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tel1">{t("form.mobile_nr")} *</Label>
                  <Input id="tel1" type="tel" value={formData.tel1} onChange={(e) => updateFormData("tel1", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fax">{t("form.fax_optional")}</Label>
                  <Input id="fax" type="tel" value={formData.fax} onChange={(e) => updateFormData("fax", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tel2">{t("form.phone_optional")}</Label>
                  <Input id="tel2" type="tel" value={formData.tel2} onChange={(e) => updateFormData("tel2", e.target.value)} />
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="address_1">{t("form.street_house")} *</Label>
                  <Input id="address_1" value={formData.address_1} onChange={(e) => updateFormData("address_1", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="postal_code">{t("form.postal_code")} *</Label>
                  <Input id="postal_code" value={formData.postal_code} onChange={(e) => updateFormData("postal_code", e.target.value)} required />
                </div>

                <div className="space-y-2">
                  <Autocomplete
                    id="country"
                    label={t("country") + " *"}
                    className="h-10 w-full"
                    value={countries.find((item) => item.id.toString() === formData.country)}
                    onChange={(newValue) => {
                      if (!newValue) return;
                      fetchCity(newValue.name);
                      updateFormData("country", newValue.id.toString());
                    }}
                    disableClearable
                    options={countries}
                    getOptionLabel={(option) => option?.name}
                    getOptionValue={(option) => option?.id.toString()}
                  />
                </div>

                <div>
                  <CityAutoComplete
                    id="city"
                    value={Number(formData.city)}
                    cities={cities}
                    required={false}
                    onSelect={(value) => {
                      updateFormData("city", value.value.toString());
                    }}
                  />
                </div>
              </div>
            )}

            {currentStep === 3 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="inv_period">Abrechnungsart *</Label>
                  <Select value={formData.inv_period} onValueChange={(value) => updateFormData("inv_period", value)}>
                    <SelectTrigger id="inv_period" className={cn("h-10", !formData.inv_period ? "text-muted-foreground" : "")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {periods.map((period) => (
                        <SelectItem key={period.value} value={period.value}>
                          {period.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="store_type">Ladentyp *</Label>
                  <Select value={formData.store_type} onValueChange={(value) => updateFormData("store_type", value)}>
                    <SelectTrigger id="store_type" className={cn("h-10", !formData.store_type ? "text-muted-foreground" : "")}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {types.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          {type.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mwst_office">Finanzamt (optional)</Label>
                  <Input id="mwst_office" value={formData.mwst_office} onChange={(e) => updateFormData("mwst_office", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mwst_number">Umsatzsteuer-Identifikationsnummer (optional)</Label>
                  <Input id="mwst_number" value={formData.mwst_number} onChange={(e) => updateFormData("mwst_number", e.target.value)} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="identity_document">{t("form.id_document")} *</Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <DocumentInput id="identity_document" loader={loader} value={formData.identity_document} onChange={handleFileChange} label={t("form.front_side")} t={t} />
                    <DocumentInput id="identity_document_2" loader={loader} value={formData.identity_document_2} onChange={handleFileChange} label={t("form.back_side")} t={t} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="tax_document">{t("form.tax_registration")}</Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <DocumentInput id="tax_document" loader={loader} value={formData.tax_document} onChange={handleFileChange} label={t("form.first_page")} t={t} />
                    <DocumentInput id="tax_document_2" loader={loader} value={formData.tax_document_2} onChange={handleFileChange} label={t("form.second_page")} t={t} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="other_document">{t("form.other_documents")}</Label>
                  <div className="grid md:grid-cols-2 gap-2">
                    <DocumentInput id="other_document" loader={loader} value={formData.other_document} onChange={handleFileChange} label={t("form.first_page")} t={t} />
                    <DocumentInput id="other_document_2" loader={loader} value={formData.other_document_2} onChange={handleFileChange} label={t("form.second_page")} t={t} />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="note">Note</Label>
                  <Textarea
                    id="note"
                    value={formData.note}
                    onChange={(e) => updateFormData("note", e.target.value)}
                    maxLength={250}
                    className="flex w-full bg-input text-foreground border rounded-lg px-3 py-3 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
                  />
                  <p className="text-xs text-muted-foreground">max. 250 Zeichen</p>
                </div>

                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    <strong>Hinweis:</strong> Alle hochgeladenen Dokumente werden sicher verschlüsselt und gemäß den Datenschutzbestimmungen verarbeitet.
                  </p>
                </div>
              </div>
            )}

            {currentStep === 4 && (
              <div className="py-12 text-center space-y-6">
                <div className="flex justify-center">
                  <div className="w-20 h-20 rounded-full bg-primary/10 flex items-center justify-center">
                    <CheckCircle2 className="w-12 h-12 text-primary" />
                  </div>
                </div>

                <div className="space-y-3">
                  <h2 className="text-3xl font-bold text-foreground">{t("success.title")}</h2>
                  <p className="text-lg text-muted-foreground max-w-md mx-auto">{t("success.message")}</p>
                </div>

                <Button asChild className="mt-6">
                  <Link href="/">{tGeneral("home_button")}</Link>
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              {currentStep !== 4 && (
                <Button type="button" variant="outline" onClick={prevStep} disabled={currentStep === 1} loading={!!loader}>
                  {t("nav.back")}
                </Button>
              )}

              {currentStep < 3 ? (
                <Button type="button" onClick={nextStep} loading={!!loader}>
                  {t("nav.next")}
                </Button>
              ) : (
                currentStep !== 4 && (
                  <Button type="submit" className="bg-primary hover:bg-primary/90" loading={!!loader} disabled={!formData.inv_period}>
                    {t("nav.complete")}
                  </Button>
                )
              )}
            </div>
          </CardContent>
        </Card>
      </form>
      {currentStep !== 4 && (
        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            {t("footer.already_have_account")}{" "}
            <Link href="/Auth/Login" className="text-primary hover:underline font-medium">
              {t("footer.login_now")}
            </Link>
          </p>
        </div>
      )}
    </div>
  );
};

type DocumentInputProps = {
  id: documentTypes;
  loader: string | boolean;
  value: number | null;
  onChange: (value: documentTypes, file: File | null) => void;
  label: string;
  t: any;
};

const DocumentInput = ({ id, loader, value, onChange, label, t }: DocumentInputProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputValue = useMemo(() => (value ? inputRef.current?.files?.[0] : null), [value]);

  return (
    <div>
      <Label htmlFor={id} className="font-normal">
        {label}
      </Label>
      <Label id={`${id}_label`} htmlFor={id} className="border-2 border-dashed border-border rounded-lg p-4 text-center hover:border-primary transition-colors">
        {loader === id ? (
          <Loader2 className="w-8 h-8 mx-auto mb-2 animate-spin" />
        ) : value ? (
          <Check className="w-8 h-8 mx-auto mb-2 text-success" />
        ) : (
          <Upload className="w-8 h-8 mx-auto mb-2 text-muted-foreground" />
        )}
        <Input ref={inputRef} id={id} type="file" className="hidden" disabled={!!loader} onChange={(e) => onChange(id, e.target.files?.[0] || null)} accept=".pdf,.jpg,.jpeg,.png" />
        {inputValue ? (
          getFileCategory(inputValue) === "image" ? (
            <div>
              <Image src={URL.createObjectURL(inputValue)} alt="uploaded file" className="w-full max-h-[150px] object-contain" width={300} height={150} />
              <p className="text-xs text-muted-foreground mt-1">{t("form.uploaded")} ({formatFileSize(inputValue.size)})</p>
            </div>
          ) : (
            <div>
              <Link href={URL.createObjectURL(inputValue)} className="text-sm inline-flex items-center gap-2 text-info hover:text-info/50 " target="_blank">
                <FileText className="size-5 mx-auto" />
                {inputValue.name}
              </Link>
              <p className="text-xs text-muted-foreground mt-1">{t("form.uploaded")} ({formatFileSize(inputValue.size)})</p>
            </div>
          )
        ) : (
          <div className="cursor-pointer">
            <span className="text-sm text-muted-foreground">{value ? t("form.uploaded") : t("form.click_or_drag")}</span>
            <p className="text-xs text-muted-foreground mt-1">{t("form.file_formats")}</p>
          </div>
        )}
      </Label>
    </div>
  );
};

function getFileCategory(file: File | null) {
  if (!file) return null;

  if (file.type.startsWith("image/")) return "image";
  if (file.type === "application/pdf") return "document";

  return "unknown";
}

export default RegisterForm;
